/**
 * CartSidebar — v3 port of the SPFx sticky-right-rail cart summary.
 *
 * Source (read-only):
 *   C:/Projects/retail-account-opening/spfx-account-opening-v2/.../components/cart/CartSidebar.tsx
 *
 * Port differences:
 *   - Reads from CartContext (`useCart()`) instead of the SPFx CartReducer.
 *   - People / products / services counts are derived from the stringified
 *     JSON envelopes (`peopleJson`, `productsJson`, `metadataJson.services`)
 *     because v3 doesn't keep those domains as top-level cart fields.
 *   - Uses PCB brand CSS tokens instead of inline hex. No fonts/colors are
 *     imported from a CDN (Global Rule).
 *   - Notes live under `cart.notes` (CartDto already declares them).
 *     Append goes through `patch({ metadataJson: … })` — the Function
 *     normalizes to the SQL `ao_CartNotes` side-table on save.
 *   - Versioning/auto-save text reads from `lastSavedAt` + `dirty` /
 *     `state === 'saving'` in CartContext.
 */

import { useMemo, useState } from 'react';
import type { CartDto, CartNote, Product } from '@retail-ops/shared-types';
import { Button } from '@/components/primitives';
import { useCart } from '@/contexts/CartContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';

// ─── Internal helpers ───────────────────────────────────────────────────────

/**
 * Emoji for a people/cart entity-type label — matches EntityCategoryPicker.
 * Native Unicode; wrapped in aria-hidden at render site.
 */
function entityEmoji(customerType: string): string {
  const t = (customerType || '').toLowerCase();
  if (t === 'business' || t === 'corporation' || t === 'corp' || t === 'llc' ||
      t === 'partnership' || t === 'nonprofit' || t === 'dba' ||
      t === 'unincorpassoc' || t === 'iolta' || t === 'political' ||
      t === 'ics_business') return '🏢';
  if (t === 'trust' || t === 'revocabletrust' || t === 'irrevocabletrust' ||
      t === 'qit' || t === 'selfsettledsnt') return '⚖️';
  if (t === 'estate') return '📜';
  if (t === 'fiduciary' || t === 'reppayee' || t === 'conservator' ||
      t === 'vafiduciary' || t === 'medicalsetaside' || t === 'usdagrant') return '🛡️';
  if (t === 'publicfunds' || t === 'government' || t === 'donation' ||
      t === 'ics_personal') return '🏛️';
  return '👤'; // individual / minor / noncitizen / utma / default
}

/**
 * Humanized label for a product acctType / category key. Same closed key set
 * as productEmoji below (Core Director acctType codes per
 * ProductTilesPickerModal.ACCT_TYPE_FOR_CATEGORY + ProductCard's emoji map).
 * Unknown keys pass through verbatim (legacy category strings already read fine).
 */
function acctTypeDisplay(raw: string): string {
  const k = (raw || '').toUpperCase();
  if (k === '10' || k === 'DDA') return 'Checking';
  if (k === '20' || k === 'SAV') return 'Savings';
  if (k === '30' || k === 'CD')  return 'Certificate (CD)';
  if (k === '40' || k === 'LOAN' || k === 'LON') return 'Loan';
  if (k === '50' || k === 'SDB' || k === 'BOX')  return 'Safe Deposit Box';
  if (k === '60' || k === 'IRA') return 'IRA';
  if (k === '91' || k === 'ATM') return 'ATM card';
  return raw;
}

/**
 * Emoji for a product acctType / category string — mirrors ProductCard.
 */
function productEmoji(raw: string): string {
  const k = (raw || '').toUpperCase();
  if (k === '10' || k === 'DDA') return '💳';
  if (k === '20' || k === 'SAV') return '💰';
  if (k === '30' || k === 'CD')  return '📈';
  if (k === '40' || k === 'LOAN' || k === 'LON') return '📝';
  if (k === '50' || k === 'SDB' || k === 'BOX')  return '🔒';
  if (k === '60' || k === 'IRA') return '🏦';
  if (k === '91' || k === 'ATM') return '🏧';
  return '📦';
}


interface CountSet {
  people: number;
  products: number;
  services: number;
  beneficiaries: number;
}

function countsFromCart(cart: CartDto): CountSet {
  return {
    people: safeLen(cart.peopleJson, 'people'),
    products: safeLen(cart.productsJson, 'products'),
    services: safeNestedLen(cart.metadataJson, 'services'),
    beneficiaries: safeBeneficiaryCount(cart.metadataJson),
  };
}

/**
 * Count beneficiaries across all products. cart.metadataJson.beneficiaries is a
 * MAP keyed by productCode (Record<productCode, BeneficiaryEntry[]>), NOT a flat
 * array — safeNestedLen returned 0 for it. Sum the per-product arrays.
 * (v0.24.1 BACKLOG-0721a — paired with the AddToCart writer that populates it.)
 */
function safeBeneficiaryCount(json: string | undefined): number {
  if (!json) return 0;
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    const map = raw?.['beneficiaries'];
    if (Array.isArray(map)) return map.length; // legacy flat-array tolerance
    if (map && typeof map === 'object') {
      return Object.values(map as Record<string, unknown>)
        .reduce<number>((n, v) => n + (Array.isArray(v) ? v.length : 0), 0);
    }
  } catch {
    return 0;
  }
  return 0;
}

function safeLen(json: string | undefined, key?: string): number {
  if (!json) return 0;
  try {
    const raw = JSON.parse(json) as unknown;
    if (Array.isArray(raw)) return raw.length;
    if (key && raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>)[key])) {
      return ((raw as Record<string, unknown>)[key] as unknown[]).length;
    }
  } catch {
    return 0;
  }
  return 0;
}

function safeNestedLen(json: string | undefined, key: string): number {
  if (!json) return 0;
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    const arr = raw?.[key];
    return Array.isArray(arr) ? arr.length : 0;
  } catch {
    return 0;
  }
}

function formatTime(d: Date | null): string {
  if (!d) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Layout primitives ─────────────────────────────────────────────────────

interface SidebarSectionProps {
  title: string;
  count: number;
  children: React.ReactNode;
}

function SidebarSection({ title, count, children }: SidebarSectionProps): JSX.Element {
  return (
    <div className="rao-sidebar__section">
      <div className="rao-sidebar__section-head">
        <span className="rao-sidebar__section-title">{title}</span>
        <span className="rao-sidebar__section-count">{count}</span>
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="rao-sidebar__empty">{children}</div>;
}

// ─── Main ───────────────────────────────────────────────────────────────────

export interface CartSidebarProps {
  /** When provided, rendered as the `Review` CTA at the bottom. */
  onGoReview?: () => void;
  /** When provided, rendered under Review — saves and closes back to dashboard. */
  onSaveAndClose?: () => void;
  /** Whether the Save & Close button should spin. */
  saving?: boolean;
}

const EMPTY_COUNTS: CountSet = { people: 0, products: 0, services: 0, beneficiaries: 0 };

export function CartSidebar(props: CartSidebarProps = {}): JSX.Element | null {
  const { cart, state, dirty, lastSavedAt, patch, flush, adopt } = useCart();
  const { user } = useAuth();
  const { config } = useConfig();

  // RULES-OF-HOOKS: every hook below must run BEFORE the `!cart` early
  // return. Pre-fix the early return sat above these useMemo/useState calls,
  // so the first cart adoption after a null-cart render threw React's
  // "Rendered more hooks than during the previous render" invariant and
  // killed the whole route subtree (red ErrorBoundary box).
  const counts = useMemo(() => (cart ? countsFromCart(cart) : EMPTY_COUNTS), [cart]);
  const peopleLabels = useMemo(() => (cart ? parsePeopleLabels(cart) : []), [cart]);
  const productLabels = useMemo(
    () => (cart ? parseProductLabels(cart, config.products ?? []) : []),
    [cart, config.products],
  );

  // Notes: append via local state, flushed into metadataJson on Add.
  const [noteDraft, setNoteDraft] = useState<string>('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const apiClient = useApiClient();

  if (!cart) return null;

  // Notes round-trip through metadataJson.notes (addNote writes there and the
  // server preserves it via the shallow metadata merge). Reading cart.notes —
  // which is projected from the never-written NotesJson column — always came
  // back empty, so every saved note vanished on reload (Bill v0.70.0 walk /
  // BACKLOG-0839). Read from the same place we write; keep cart.notes as a
  // fallback for any legacy NotesJson-populated cart.
  const mdNotes = safeParseObject(cart.metadataJson)['notes'];
  const notes: ReadonlyArray<CartNote> = Array.isArray(mdNotes)
    ? (mdNotes as CartNote[])
    : (cart.notes ?? []);

  // Bill 2026-10-15: a note is appended by its own route, so it saves at every stage (including
  // while the application is locked for signing) and never rewrites the rest of the metadata.
  const addNote = async (): Promise<void> => {
    const trimmed = noteDraft.trim();
    if (!trimmed || noteSaving) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      const res = await apiClient.retail.carts.addNote(cart.cartId, {
        text: trimmed,
        ...(user?.upn ? { author: user.upn } : {}),
      });
      if (res.success && res.cart) {
        adopt(res.cart);
        setNoteDraft('');
      } else {
        setNoteError(res.error ?? "The note couldn't be saved. Try again.");
      }
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : "The note couldn't be saved. Try again.");
    } finally {
      setNoteSaving(false);
    }
  };

  const saveLabel = state === 'saving'
    ? 'Saving…'
    : dirty
      ? 'Unsaved changes'
      : lastSavedAt
        ? `Auto-saved ${formatTime(lastSavedAt)}`
        : 'Saved';

  return (
    <aside className="rao-sidebar" aria-label="Application summary" data-tour="cart-sidebar">
      <header className="rao-sidebar__header">
        <h3 className="rao-sidebar__title">Application</h3>
      </header>

      <div className="rao-sidebar__meta">
        <span>Officer <strong>{cart.officerCode}</strong></span>
        <span>· Branch {cart.branch}</span>
      </div>

      <SidebarSection title="People" count={counts.people}>
        {counts.people === 0 ? (
          <EmptyHint>No people added yet</EmptyHint>
        ) : (
          peopleLabels.map((p, i) => (
            <div key={i} className="rao-sidebar__item">
              <div className="rao-sidebar__item-label">{p.label}</div>
              <div className="rao-sidebar__item-sub">
                <span aria-hidden="true" style={{ marginRight: 4 }}>
                  {entityEmoji(p.sub)}
                </span>
                {p.sub}
              </div>
            </div>
          ))
        )}
      </SidebarSection>

      <SidebarSection title="Products" count={counts.products}>
        {counts.products === 0 ? (
          <EmptyHint>No products selected</EmptyHint>
        ) : (
          productLabels.map((p) => (
            <div key={p.key} className="rao-sidebar__item">
              <div className="rao-sidebar__item-label">{p.label}</div>
              <div className="rao-sidebar__item-sub">
                <span aria-hidden="true" style={{ marginRight: 4 }}>
                  {productEmoji(p.sub)}
                </span>
                {acctTypeDisplay(p.sub)}
              </div>
            </div>
          ))
        )}
      </SidebarSection>

      <SidebarSection title="Notes" count={notes.length}>
        {notes.length === 0 ? (
          <EmptyHint>No notes yet</EmptyHint>
        ) : (
          <div className="rao-sidebar__notes">
            {notes.map((n, i) => (
              <div key={i} className="rao-sidebar__note">
                <div className="rao-sidebar__note-head">
                  <strong>{n.author}</strong>
                  <span>· {new Date(n.timestamp).toLocaleString()}</span>
                </div>
                <div className="rao-sidebar__note-text">{n.text}</div>
              </div>
            ))}
          </div>
        )}
        <textarea
          className="rao-sidebar__note-input"
          value={noteDraft}
          placeholder="Add a note…"
          rows={2}
          onChange={(e) => setNoteDraft(e.target.value)}
        />
        <Button
          variant="ghost"
          size="sm"
          disabled={!noteDraft.trim() || noteSaving}
          onClick={() => { void addNote(); }}
        >
          {noteSaving ? 'Saving…' : 'Add note'}
        </Button>
        {noteError && <div role="alert" style={{ fontSize: 12, color: 'var(--pcb-red, #b10505)' }}>{noteError}</div>}
      </SidebarSection>

      <div className="rao-sidebar__save">{saveLabel}</div>

      {props.onGoReview && (
        <Button
          variant="primary"
          disabled={counts.people === 0 || counts.products === 0}
          onClick={props.onGoReview}
        >
          Review
        </Button>
      )}

      {props.onSaveAndClose && (
        <Button
          variant="ghost"
          disabled={props.saving}
          onClick={async () => {
            await flush();
            props.onSaveAndClose?.();
          }}
        >
          {props.saving ? 'Saving…' : 'Save & Close'}
        </Button>
      )}
    </aside>
  );
}

// ─── Label parsers ──────────────────────────────────────────────────────────

interface LabelPair {
  label: string;
  sub: string;
}

/** Product row with a stable identity for React keys (row-store P2). */
interface ProductLabelRow extends LabelPair {
  /** Line-item instanceId when present; index-suffixed code fallback for legacy rows. */
  key: string;
}

function parsePeopleLabels(cart: CartDto): ReadonlyArray<LabelPair> {
  const people = safeParseArray(cart.peopleJson, 'people');
  return people.map((p) => ({
    label: String((p['fullName'] ?? `${p['firstName'] ?? ''} ${p['lastName'] ?? ''}`.trim()) || '(unnamed)'),
    sub: String(p['customerType'] ?? p['role'] ?? 'Individual'),
  }));
}

function parseProductLabels(
  cart: CartDto,
  productDefs: ReadonlyArray<Product>,
): ReadonlyArray<ProductLabelRow> {
  // Canonical cart line shape is `CartSelectedProduct` ({ code, qty, … } —
  // shared-types cart.ts, written by ProductsStep.onCommitModal). The row
  // carries only the product CODE; name + acctType resolve through the
  // /v3/config/products catalog, exactly like ProductsStep's card stack.
  // Pre-fix this reader looked for `productCode` / inline `definition`
  // fields that the canonical shape never had, so every product rendered
  // as the "Product" placeholder. Legacy SPFx rows are still tolerated.
  // Row-store P2 (2026-07-06): lines now carry `instanceId` — two instances
  // of the SAME code are distinct rows keyed by instance id (never collapsed).
  const byCode = new Map(productDefs.map((d) => [d.code, d] as const));
  const products = safeParseArray(cart.productsJson, 'products');
  return products.map((pr, i) => {
    const legacyDef = pr['definition'] as Record<string, unknown> | undefined;
    const code = typeof pr['code'] === 'string'
      ? pr['code']
      : typeof pr['productCode'] === 'string' ? pr['productCode'] : '';
    const instanceId = typeof pr['instanceId'] === 'string' && pr['instanceId'].length > 0
      ? pr['instanceId']
      : undefined;
    const def = code ? byCode.get(code) : undefined;
    const label = def?.name
      ?? (typeof legacyDef?.['name'] === 'string' ? (legacyDef['name'] as string) : undefined)
      ?? (typeof pr['name'] === 'string' ? (pr['name'] as string) : undefined)
      ?? (code || 'Product');
    const sub = def?.acctType
      ?? (typeof legacyDef?.['category'] === 'string' ? (legacyDef['category'] as string) : undefined)
      ?? (typeof pr['category'] === 'string' ? (pr['category'] as string) : undefined)
      ?? code;
    return { key: instanceId ?? `${code || 'product'}-${i}`, label: String(label), sub: String(sub) };
  });
}

function safeParseArray(json: string | undefined, key?: string): Array<Record<string, unknown>> {
  if (!json) return [];
  try {
    const raw = JSON.parse(json) as unknown;
    if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
    if (key && raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>)[key])) {
      return (raw as Record<string, unknown>)[key] as Array<Record<string, unknown>>;
    }
  } catch {
    return [];
  }
  return [];
}

function safeParseObject(json: string | undefined): Record<string, unknown> {
  if (!json) return {};
  try {
    const raw = JSON.parse(json) as unknown;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return raw as Record<string, unknown>;
    }
  } catch {
    // fall through
  }
  return {};
}

export default CartSidebar;
